import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import Navbar from "../components/Navbar";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";

const PAGE_TABS = [
  { key: "home", label: "Home page" },
  { key: "search", label: "Browse / Search page" },
];

const FIELDS = [
  { key: "title", label: "Page title (<title>)", type: "text", hint: "60 chars ideal, appended with | InstaVivah" },
  { key: "description", label: "Meta description", type: "textarea", hint: "≤ 160 chars for Google snippet" },
  { key: "keywords", label: "Meta keywords", type: "text", hint: "comma-separated" },
  { key: "canonical_path", label: "Canonical path", type: "text", hint: "e.g. / or /browse" },
  { key: "robots", label: "Robots directive", type: "text", hint: "index, follow" },
  { key: "og_title", label: "Open Graph title (Facebook, WhatsApp)", type: "text" },
  { key: "og_description", label: "Open Graph description", type: "textarea" },
  { key: "og_image", label: "Open Graph image URL (1200×630)", type: "text" },
  { key: "og_type", label: "og:type", type: "text", hint: "website / article" },
  { key: "twitter_card", label: "Twitter card type", type: "text", hint: "summary_large_image" },
  { key: "twitter_title", label: "Twitter title", type: "text" },
  { key: "twitter_description", label: "Twitter description", type: "textarea" },
  { key: "twitter_image", label: "Twitter image URL", type: "text" },
  { key: "structured_data", label: "Additional JSON-LD (optional)", type: "textarea", hint: "Raw JSON string, leave empty to skip" },
];

export default function AdminSeo() {
  const { user, ready } = useAuth();
  const [tab, setTab] = useState("home");
  const [drafts, setDrafts] = useState({}); // page_key -> settings
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/admin/seo");
        const map = {};
        (data || []).forEach((row) => {
          map[row.page_key] = row;
        });
        setDrafts(map);
      } catch (e) {
        toast.error(formatApiError(e));
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const current = drafts[tab];
  const updateField = (k, v) => {
    setDrafts((d) => ({ ...d, [tab]: { ...(d[tab] || {}), [k]: v } }));
  };

  const save = async () => {
    if (!current) return;
    setSaving(true);
    try {
      const payload = {};
      FIELDS.forEach(({ key }) => {
        payload[key] = current[key] ?? "";
      });
      const { data } = await api.put(`/admin/seo/${tab}`, payload);
      setDrafts((d) => ({ ...d, [tab]: data }));
      toast.success("Saved");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  if (!ready) return null;
  if (!user || user.role !== "admin") {
    return (
      <div>
        <Navbar />
        <div className="container py-5">
          <div className="sb-card text-center">
            <h1 className="h4">Admin access required</h1>
            <p className="text-muted">
              You must sign in as an administrator to manage SEO settings.
            </p>
            <Link to="/login" className="btn btn-primary" data-testid="admin-seo-login">
              Login as admin
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <Navbar />
      <div className="container py-4" data-testid="admin-seo-page">
        <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
          <div>
            <div className="sb-kicker mb-1">Admin</div>
            <h1 className="h3 mb-0">SEO &amp; Social Sharing</h1>
          </div>
          {current?.updated_at && (
            <span className="text-muted small" data-testid="seo-updated-at">
              Last saved: {new Date(current.updated_at).toLocaleString()}
            </span>
          )}
        </div>

        {/* Tabs */}
        <div className="sb-steps mb-3" role="tablist" data-testid="seo-tabs">
          {PAGE_TABS.map((t) => (
            <button
              key={t.key}
              type="button"
              className={"sb-step" + (tab === t.key ? " active" : "")}
              onClick={() => setTab(t.key)}
              data-testid={`seo-tab-${t.key}`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="sb-card" data-testid="seo-loading">Loading…</div>
        ) : !current ? (
          <div className="sb-card">No settings found.</div>
        ) : (
          <div className="row g-4">
            <div className="col-lg-8">
              <div className="sb-card">
                <div className="row g-3">
                  {FIELDS.map((f) => (
                    <div className="col-12" key={f.key}>
                      <label className="form-label">
                        {f.label}
                      </label>
                      {f.type === "textarea" ? (
                        <textarea
                          rows={3}
                          className="form-control"
                          value={current[f.key] ?? ""}
                          onChange={(e) => updateField(f.key, e.target.value)}
                          data-testid={`seo-field-${f.key}`}
                        />
                      ) : (
                        <input
                          type="text"
                          className="form-control"
                          value={current[f.key] ?? ""}
                          onChange={(e) => updateField(f.key, e.target.value)}
                          data-testid={`seo-field-${f.key}`}
                        />
                      )}
                      {f.hint && (
                        <div className="form-text">{f.hint}</div>
                      )}
                    </div>
                  ))}
                </div>
                <div className="d-flex justify-content-end mt-3">
                  <button
                    className="btn btn-primary"
                    onClick={save}
                    disabled={saving}
                    data-testid="seo-save"
                  >
                    {saving ? "Saving…" : "Save changes"}
                  </button>
                </div>
              </div>
            </div>

            {/* Preview card */}
            <div className="col-lg-4">
              <div
                className="sb-card"
                data-testid="seo-preview"
                style={{ position: "sticky", top: 90 }}
              >
                <div className="sb-kicker mb-2">Social preview</div>
                <div
                  style={{
                    border: "1px solid var(--sb-border, #e5e0d8)",
                    borderRadius: 12,
                    overflow: "hidden",
                    background: "#fff",
                  }}
                >
                  {current.og_image ? (
                    <img
                      src={current.og_image}
                      alt=""
                      style={{
                        display: "block",
                        width: "100%",
                        aspectRatio: "1200/630",
                        objectFit: "cover",
                      }}
                    />
                  ) : (
                    <div
                      style={{
                        aspectRatio: "1200/630",
                        background: "#f4eee6",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        color: "#a89b90",
                        fontSize: 12,
                      }}
                    >
                      1200 × 630 OG image
                    </div>
                  )}
                  <div style={{ padding: 12 }}>
                    <div
                      style={{
                        textTransform: "uppercase",
                        letterSpacing: 1,
                        fontSize: 11,
                        color: "#8a817c",
                      }}
                    >
                      instavivah
                    </div>
                    <div
                      style={{
                        fontWeight: 700,
                        fontSize: 15,
                        lineHeight: 1.35,
                        margin: "4px 0 6px",
                      }}
                    >
                      {current.og_title || current.title || "Untitled"}
                    </div>
                    <div style={{ color: "#4a4542", fontSize: 12.5, lineHeight: 1.45 }}>
                      {current.og_description ||
                        current.description ||
                        "Add a description above."}
                    </div>
                  </div>
                </div>

                <div className="mt-3 small text-muted">
                  Note: OG / Twitter previews on WhatsApp, Facebook &amp; X
                  may be cached — use their scraper / card validator tools
                  to refresh after saving.
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
